import React, {
  CSSProperties,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { classNames } from "components/utils";

interface PersistentDeployActionProps {
  children: ReactNode | ((isPinned: boolean) => ReactNode);
}

interface PinnedPosition {
  height: number;
  left: number;
  top: number;
  width: number;
}

const DEFAULT_NAVBAR_HEIGHT = 48;
const STICKY_HEADER_MEDIA_QUERY = "(min-width: 640px)";

const getNavbarOffset = (element: HTMLElement) => {
  const configuredOffset = Number.parseFloat(
    getComputedStyle(element).getPropertyValue("--admin-navigation-top-offset"),
  );

  return Number.isFinite(configuredOffset)
    ? configuredOffset
    : DEFAULT_NAVBAR_HEIGHT;
};

export const PersistentDeployAction: React.FC<PersistentDeployActionProps> = ({
  children,
}) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const actionRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number>();
  const [pinnedPosition, setPinnedPosition] = useState<PinnedPosition | null>(
    null,
  );
  const [canPersist, setCanPersist] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia(STICKY_HEADER_MEDIA_QUERY).matches,
  );
  const shouldPersistPastContainer = canPersist;

  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return;
    }

    const mediaQuery = window.matchMedia(STICKY_HEADER_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setCanPersist(event.matches);
    };
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    if (!shouldPersistPastContainer) return;

    const updatePosition = () => {
      frameRef.current = undefined;
      const anchor = anchorRef.current;
      const action = actionRef.current;
      if (!anchor || !action) return;

      const anchorRect = anchor.getBoundingClientRect();
      const navbarOffset = getNavbarOffset(anchor);
      const shouldPin = anchorRect.top <= navbarOffset;

      setPinnedPosition((current) => {
        if (!shouldPin) return current ? null : current;

        const next = {
          height: action.getBoundingClientRect().height,
          left: anchorRect.left,
          top: navbarOffset,
          width: anchorRect.width,
        };

        if (
          current?.height === next.height &&
          current.left === next.left &&
          current.top === next.top &&
          current.width === next.width
        ) {
          return current;
        }
        return next;
      });
    };

    const scheduleUpdate = () => {
      if (frameRef.current !== undefined) return;
      frameRef.current = requestAnimationFrame(updatePosition);
    };

    updatePosition();
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    const resizeObserver = new ResizeObserver(scheduleUpdate);
    if (anchorRef.current) resizeObserver.observe(anchorRef.current);
    if (actionRef.current) resizeObserver.observe(actionRef.current);

    return () => {
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      resizeObserver.disconnect();
      if (frameRef.current !== undefined) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [shouldPersistPastContainer]);

  const activePinnedPosition = shouldPersistPastContainer
    ? pinnedPosition
    : null;
  const pinnedStyle: CSSProperties | undefined = activePinnedPosition
    ? {
        left: activePinnedPosition.left,
        top: activePinnedPosition.top,
        width: activePinnedPosition.width,
      }
    : undefined;

  return (
    <div
      ref={anchorRef}
      style={
        activePinnedPosition
          ? { height: activePinnedPosition.height }
          : undefined
      }
    >
      <div
        ref={actionRef}
        className={classNames(
          "z-20 shrink-0 overflow-hidden transition-[background-color,border-color,box-shadow] duration-200",
          activePinnedPosition
            ? "fixed z-40 rounded-lg border border-zinc-700 bg-zinc-950/95 shadow-[0_12px_24px_rgba(0,0,0,0.24)]"
            : canPersist
              ? "sticky top-[var(--admin-navigation-top-offset,48px)] rounded-t-[7px] border-zinc-800 bg-transparent shadow-none"
              : "relative rounded-t-[7px] border-zinc-800 bg-transparent shadow-none",
        )}
        style={pinnedStyle}
      >
        {typeof children === "function"
          ? children(Boolean(activePinnedPosition))
          : children}
      </div>
    </div>
  );
};
